import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";

import { GetProductOptions } from "../utils/product";


const COLOR_MAP = {
  "שחור": "#000000",
  "לבן": "#ffffff",
  "אפור": "#9e9e9e",
  "כחול": "#1e88e5",
  "אדום": "#e53935",
  "ורוד": "#ff69b4",
  "ירוק": "#43a047",
  "חום": "#6d4c41",
  "צהוב": "#fdd835",
  "כתום": "#fb8c00",
  "סגול": "#8e24aa",
  "תכלת": "#4fc3f7",

  "ורוד פוקסיה": "#ff1493",
  "טורקיז": "#246d66",
  "ירוק כהה": "#1b5e20",
  "סגול בהיר": "#ce93d8",
  "ורוד בהיר": "#f8bbd0",
  "כסף": "#c0c0c0",
  "זהב": "#e6c558",
  "נחושת": "#b87333",
  "ארד": "#cd7f32",
};


function getField(option, name) {
  return (
    option?.[name] ??
    option?.[
      name.charAt(0).toLowerCase() +
      name.slice(1)
    ]
  );
}


function normalizeOption(option) {
  return {
    id: getField(option, "Id"),

    optionType:
      getField(option, "OptionType"),

    value: String(
      getField(option, "Value") ?? ""
    ).trim(),

    extraPrice:
      getField(option, "ExtraPrice"),

    quantity:
      getField(option, "Quantity"),

    image:
      getField(option, "Image"),

    /*
     * חשוב:
     * אם השדה לא הגיע מהשרת,
     * ברירת המחדל תהיה שהווריאציה במלאי.
     */
    isInStock:
      getField(option, "IsInStock") ?? true,
  };
}


function Circle({
  children,
  selected,
  onClick,
  title,
  bg,
  disabled = false,
  textColor = "#111",
}) {
  return (
    <Tooltip
      title={title || ""}
      placement="top"
    >
      <Box
        onClick={
          disabled
            ? undefined
            : onClick
        }
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          cursor: disabled
            ? "not-allowed"
            : "pointer",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: bg,
          border: selected
            ? "2px solid #000"
            : "1px solid #cfcfcf",
          boxShadow: selected ? 2 : 0,
          fontSize: 11,
          fontWeight: 700,
          color: textColor,
          opacity: disabled ? 0.35 : 1,
          position: "relative",

          "&::after": disabled
            ? {
                content: '""',
                position: "absolute",
                width: "130%",
                height: "2px",
                backgroundColor: "#d32f2f",
                transform: "rotate(-45deg)",
              }
            : {},
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}


/**
 * props:
 *
 * productId:
 * מזהה המוצר.
 *
 * onChange:
 * מחזירה את הווריאציות שנבחרו:
 * {
 *   Color?: option,
 *   Size?: option
 * }
 *
 * initialSelection:
 * מזהי הווריאציות שנבחרו מראש:
 * {
 *   Color?: optionId,
 *   Size?: optionId
 * }
 */
export default function ProductVariationPicker({
  productId,
  onChange,
  initialSelection,
}) {
  const [options, setOptions] =
    useState([]);

  const [selected, setSelected] =
    useState({});


  useEffect(() => {
    let cancelled = false;


    const load = async () => {
      try {
        const raw =
          await GetProductOptions(productId);


        const normalized =
          (raw || [])
            .map(normalizeOption)
            .filter(
              (option) =>
                option.optionType &&
                option.value
            );


        if (cancelled) {
          return;
        }


        setOptions(normalized);


        const nextSelected = {};


        const byType =
          normalized.reduce(
            (result, option) => {
              if (!result[option.optionType]) {
                result[option.optionType] = [];
              }

              result[option.optionType].push(
                option
              );

              return result;
            },
            {}
          );


        ["Color", "Size"].forEach(
          (type) => {
            const list =
              byType[type] || [];


            if (!list.length) {
              return;
            }


            const initialId =
              initialSelection?.[type];


            const found = initialId
              ? list.find(
                  (option) =>
                    String(option.id) ===
                    String(initialId)
                )
              : null;


            /*
             * מחפשים את הווריאציה הראשונה
             * שמסומנת כזמינה במלאי.
             */
            const firstAvailable =
              list.find(
                (option) =>
                  option.isInStock !== false
              );


            /*
             * אם הבחירה הראשונית קיימת ובמלאי,
             * נבחר אותה.
             *
             * אחרת נבחר את האפשרות הראשונה
             * שבמלאי.
             *
             * אם כל האפשרויות לא במלאי,
             * נשאיר את הראשונה כדי שיוצג
             * ללקוח שהווריאציה אינה זמינה.
             */
            if (
              found &&
              found.isInStock !== false
            ) {
              nextSelected[type] = found;
            } else {
              nextSelected[type] =
                firstAvailable || list[0];
            }
          }
        );


        setSelected(nextSelected);

        onChange?.(nextSelected);
      } catch (error) {
        console.error(
          "Failed to load product options:",
          error
        );

        if (!cancelled) {
          setOptions([]);
          setSelected({});
          onChange?.({});
        }
      }
    };


    if (productId) {
      load();
    } else {
      setOptions([]);
      setSelected({});
      onChange?.({});
    }


    return () => {
      cancelled = true;
    };
  }, [
    productId,
    initialSelection,
    onChange,
  ]);


  const grouped = useMemo(() => {
    const byType = {
      Color: [],
      Size: [],
    };


    for (const option of options) {
      if (
        option.optionType === "Color"
      ) {
        byType.Color.push(option);
      } else if (
        option.optionType === "Size"
      ) {
        byType.Size.push(option);
      }
    }


    return byType;
  }, [options]);


  const handlePick = (
    type,
    option
  ) => {
    /*
     * לא ניתן לבחור וריאציה
     * שאינה במלאי.
     */
    if (
      option?.isInStock === false
    ) {
      return;
    }


    const next = {
      ...selected,
      [type]: option,
    };


    setSelected(next);

    onChange?.(next);
  };


  if (
    !grouped.Color.length &&
    !grouped.Size.length
  ) {
    return null;
  }


  return (
    <Box sx={{ mt: 0 }}>
      {grouped.Color.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            sx={{ opacity: 0.8 }}
          >
            צבע
          </Typography>


          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {grouped.Color.map(
              (option) => {
                const cssColor =
                  COLOR_MAP[option.value] ||
                  option.value;


                const isSelected =
                  selected.Color?.id ===
                  option.id;


                const isAvailable =
                  option.isInStock !== false;


                return (
                  <Circle
                    key={
                      option.id ??
                      `color-${option.value}`
                    }
                    title={
                      isAvailable
                        ? option.value
                        : `${option.value} - לא במלאי`
                    }
                    selected={isSelected}
                    disabled={!isAvailable}
                    onClick={() =>
                      handlePick(
                        "Color",
                        option
                      )
                    }
                    bg={cssColor}
                    textColor="transparent"
                  >
                    .
                  </Circle>
                );
              }
            )}
          </Box>
        </Box>
      )}


      {grouped.Size.length > 0 && (
        <Box
          sx={{
            mt:
              grouped.Color.length
                ? 1
                : 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <FormControl
            size="small"
            sx={{ minWidth: 160 }}
          >
            <InputLabel
              id={`size-label-${productId}`}
            >
              לבחירה
            </InputLabel>


            <Select
              labelId={`size-label-${productId}`}
              value={
                selected.Size?.id ?? ""
              }
              label="לבחירה"
              onChange={(event) => {
                const id =
                  event.target.value;


                const option =
                  grouped.Size.find(
                    (item) =>
                      String(item.id) ===
                      String(id)
                  );


                if (option) {
                  handlePick(
                    "Size",
                    option
                  );
                }
              }}
            >
              {grouped.Size.map(
                (option) => (
                  <MenuItem
                    key={
                      option.id ??
                      `size-${option.value}`
                    }
                    value={option.id}
                    disabled={
                      option.isInStock === false
                    }
                  >
                    {option.value}

                    {option.isInStock === false
                      ? " - לא במלאי"
                      : ""}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        </Box>
      )}
    </Box>
  );
}